// src/features/surveys/surveyDefinitions.js
// The survey questions live here, separate from the app logic.
//
// Every text has an English (en) and a Hindi (hi) version. The Hindi is a
// draft (2026-10-08) — have a native speaker check it. To fix a translation,
// just edit the hi: '…' text. The language switch only appears when EVERY
// Hindi slot of a survey is filled, so learners never see a half-translated
// survey (empty any slot, e.g. hi: '', to hide Hindi again).
//
// Question types:
//   single — choose one option        → saved as a string, e.g. 'easy'
//   multi  — choose one or more       → saved as an array, e.g. ['time', 'other']
//   text   — short written answer     → saved as a string
// Option flags:
//   withText  — choosing it shows a small text box (saved as `<questionId>_other`)
//   exclusive — choosing it clears all other options (e.g. "No difficulties")

// How-do-you-learn block: same frequency scale for all four questions.
const FREQUENCY_OPTIONS = [
  { value: 'never', label: { en: 'Never', hi: 'कभी नहीं' } },
  { value: 'rarely', label: { en: 'Rarely', hi: 'शायद ही कभी' } },
  { value: 'sometimes', label: { en: 'Sometimes', hi: 'कभी-कभी' } },
  { value: 'often', label: { en: 'Often', hi: 'अक्सर' } },
  { value: 'always', label: { en: 'Always', hi: 'हमेशा' } }
];
const HOW_YOU_LEARN_HINT = {
  en: 'How do you learn? There are no right or wrong answers.',
  hi: 'आप कैसे सीखते हैं? कोई जवाब सही या गलत नहीं है।'
};

// Numeric score for frequency answers (Never = 1 … Always = 5) — for
// monthly averages in reports later.
export const FREQUENCY_SCORE = { never: 1, rarely: 2, sometimes: 3, often: 4, always: 5 };

export const MONTHLY_SURVEY = {
  id: 'monthly',
  title: { en: 'Monthly feedback', hi: 'मासिक फ़ीडबैक' },
  intro: {
    en: 'Tell us how learning went this month. It takes about 3 minutes. Only the Litvill team sees your answers — your tutor does not.',
    hi: 'बताइए कि इस महीने आपकी पढ़ाई कैसी रही। इसमें लगभग 3 मिनट लगेंगे। आपके जवाब सिर्फ़ Litvill टीम देखती है — आपके ट्यूटर नहीं।'
  },
  startLabel: { en: 'Start', hi: 'शुरू करें' },
  thankYou: {
    en: 'Thank you! Your answers help us improve the programme.',
    hi: 'धन्यवाद! आपके जवाबों से हमें कार्यक्रम को बेहतर बनाने में मदद मिलती है।'
  },
  // Dashboard invitation card (shown before the learner picks a language).
  card: {
    text: {
      en: 'How did learning go this month? About 3 minutes. Only the Litvill team sees your answers.',
      hi: 'इस महीने पढ़ाई कैसी रही? लगभग 3 मिनट। आपके जवाब सिर्फ़ Litvill टीम देखती है।'
    },
    later: { en: 'Later', hi: 'बाद में' }
  },
  // Buttons and messages inside the survey.
  ui: {
    greeting: { en: 'Hi', hi: 'नमस्ते' },
    back: { en: '← Back', hi: '← पीछे' },
    next: { en: 'Next →', hi: 'आगे →' },
    skip: { en: 'Skip →', hi: 'छोड़ें →' },
    submit: { en: 'Submit', hi: 'भेजें' },
    sending: { en: 'Sending…', hi: 'भेजा जा रहा है…' },
    close: { en: 'Close', hi: 'बंद करें' },
    otherPlaceholder: { en: 'Please tell us more (optional)', hi: 'कृपया थोड़ा और बताइए (वैकल्पिक)' },
    required: { en: 'Please choose an answer to continue.', hi: 'आगे बढ़ने के लिए कृपया एक जवाब चुनें।' },
    sendError: {
      en: 'Could not send your answers. Please check your internet and try again.',
      hi: 'आपके जवाब नहीं भेजे जा सके। कृपया इंटरनेट जाँचें और फिर से कोशिश करें।'
    }
  },
  questions: [
    {
      id: 'lms_ease',
      type: 'single',
      required: true,
      text: { en: 'How easy was it to use the LMS this month?', hi: 'इस महीने LMS का इस्तेमाल करना कितना आसान था?' },
      options: [
        { value: 'easy', label: { en: 'Easy', hi: 'आसान' } },
        { value: 'sometimes_difficult', label: { en: 'Sometimes difficult', hi: 'कभी-कभी मुश्किल' } },
        { value: 'very_difficult', label: { en: 'Very difficult', hi: 'बहुत मुश्किल' } },
        { value: 'didnt_use', label: { en: "Didn't use it", hi: 'इस्तेमाल नहीं किया' } }
      ]
    },
    {
      id: 'lesson_level',
      type: 'single',
      required: true,
      text: { en: 'How was the level of the lessons?', hi: 'पाठों का स्तर कैसा था?' },
      options: [
        { value: 'too_easy', label: { en: 'Too easy', hi: 'बहुत आसान' } },
        { value: 'about_right', label: { en: 'About right', hi: 'लगभग सही' } },
        { value: 'too_difficult', label: { en: 'Too difficult', hi: 'बहुत कठिन' } },
        { value: 'cant_judge', label: { en: "Can't judge", hi: 'कह नहीं सकते' } }
      ]
    },
    {
      id: 'tutor_helpful',
      type: 'single',
      required: true,
      text: { en: 'How helpful were your tutor sessions?', hi: 'आपके ट्यूटर सेशन कितने मददगार थे?' },
      options: [
        { value: 'helpful', label: { en: 'Helpful', hi: 'मददगार' } },
        { value: 'partly_helpful', label: { en: 'Partly helpful', hi: 'कुछ हद तक मददगार' } },
        { value: 'not_helpful', label: { en: 'Not helpful', hi: 'मददगार नहीं' } },
        { value: 'didnt_attend', label: { en: "Didn't attend", hi: 'सेशन में शामिल नहीं हुए' } }
      ]
    },
    {
      id: 'tutor_comfort',
      type: 'single',
      required: true,
      text: { en: 'How comfortable did you feel asking your tutor for help?', hi: 'अपने ट्यूटर से मदद माँगने में आपको कितना सहज महसूस हुआ?' },
      options: [
        { value: 'comfortable', label: { en: 'Comfortable', hi: 'सहज' } },
        { value: 'sometimes_uncomfortable', label: { en: 'Sometimes uncomfortable', hi: 'कभी-कभी असहज' } },
        { value: 'very_uncomfortable', label: { en: 'Very uncomfortable', hi: 'बहुत असहज' } },
        { value: 'didnt_attend', label: { en: "Didn't attend", hi: 'सेशन में शामिल नहीं हुए' } }
      ]
    },
    // ── How do you learn? (self-directed learning — added 2026-10-08) ──
    // Learners are false beginners and are free to skip and choose units;
    // these show whether they use that freedom, month by month.
    {
      id: 'choose_by_interest',
      type: 'single',
      required: true,
      scale: 'frequency',
      text: {
        en: 'In the LMS, I choose units because they interest me.',
        hi: 'LMS में, मैं वे यूनिट चुनता/चुनती हूँ जो मुझे दिलचस्प लगती हैं।'
      },
      hint: HOW_YOU_LEARN_HINT,
      options: FREQUENCY_OPTIONS
    },
    {
      id: 'skip_known',
      type: 'single',
      required: true,
      scale: 'frequency',
      text: {
        en: 'I skip units that I already know.',
        hi: 'जो यूनिट मुझे पहले से आती हैं, उन्हें मैं छोड़ देता/देती हूँ।'
      },
      hint: HOW_YOU_LEARN_HINT,
      options: FREQUENCY_OPTIONS
    },
    {
      id: 'try_another_way',
      type: 'single',
      required: true,
      scale: 'frequency',
      text: {
        en: 'When I do not understand something, I try a different way to learn it (for example, watching again, asking someone, looking it up).',
        hi: 'जब मुझे कुछ समझ नहीं आता, तो मैं उसे सीखने का कोई दूसरा तरीका आज़माता/आज़माती हूँ (जैसे, दोबारा देखना, किसी से पूछना, खोजकर देखना)।'
      },
      hint: HOW_YOU_LEARN_HINT,
      options: FREQUENCY_OPTIONS
    },
    {
      id: 'use_outside',
      type: 'single',
      required: true,
      scale: 'frequency',
      text: {
        en: 'I use what I learn outside my lessons (for example, with friends or family, on my phone, at school).',
        hi: 'जो मैं सीखता/सीखती हूँ, उसका इस्तेमाल मैं अपने पाठों के बाहर भी करता/करती हूँ (जैसे, दोस्तों या परिवार के साथ, अपने फ़ोन पर, स्कूल में)।'
      },
      hint: HOW_YOU_LEARN_HINT,
      options: FREQUENCY_OPTIONS
    },
    {
      id: 'difficulties',
      type: 'multi',
      required: true,
      text: { en: 'What made it difficult to participate this month?', hi: 'इस महीने भाग लेने में किन बातों से मुश्किल हुई?' },
      hint: { en: 'Select all that apply.', hi: 'जो भी लागू हों, सब चुनें।' },
      options: [
        { value: 'time', label: { en: 'Time', hi: 'समय की कमी' } },
        { value: 'device_access', label: { en: 'Device access', hi: 'फ़ोन / डिवाइस उपलब्ध नहीं' } },
        { value: 'internet_data', label: { en: 'Internet / data', hi: 'इंटरनेट / डेटा' } },
        { value: 'lesson_difficulty', label: { en: 'Lesson difficulty', hi: 'पाठ कठिन थे' } },
        { value: 'lms_problems', label: { en: 'LMS problems', hi: 'LMS में समस्याएँ' } },
        { value: 'session_timings', label: { en: 'Session timings', hi: 'सेशन का समय' } },
        { value: 'other', label: { en: 'Other', hi: 'अन्य' }, withText: true },
        { value: 'none', label: { en: 'No difficulties', hi: 'कोई मुश्किल नहीं' }, exclusive: true }
      ]
    },
    {
      id: 'helped_most',
      type: 'text',
      required: false,
      text: { en: 'What helped you learn most this month?', hi: 'इस महीने आपको सीखने में सबसे ज़्यादा किस चीज़ से मदद मिली?' },
      hint: { en: 'Optional', hi: 'वैकल्पिक' }
    },
    {
      id: 'improve',
      type: 'text',
      required: false,
      text: { en: 'What should we improve?', hi: 'हमें क्या बेहतर करना चाहिए?' },
      hint: { en: 'Optional', hi: 'वैकल्पिक' }
    },
    {
      id: 'continue',
      type: 'single',
      required: true,
      text: { en: 'How do you feel about continuing next month?', hi: 'अगले महीने जारी रखने के बारे में आप क्या सोचते हैं?' },
      options: [
        { value: 'want_continue', label: { en: 'Want to continue', hi: 'जारी रखना चाहते हैं' } },
        { value: 'unsure', label: { en: 'Unsure', hi: 'पक्का नहीं है' } },
        { value: 'considering_stopping', label: { en: 'Considering stopping', hi: 'छोड़ने के बारे में सोच रहे हैं' } }
      ]
    },
    {
      id: 'contact',
      type: 'single',
      required: true,
      text: { en: 'Would you like someone from our team to contact you?', hi: 'क्या आप चाहते हैं कि हमारी टीम से कोई आपसे संपर्क करे?' },
      options: [
        { value: 'yes', label: { en: 'Yes', hi: 'हाँ' } },
        { value: 'no', label: { en: 'No', hi: 'नहीं' } }
      ]
    }
  ],

  // Which answers put a learner on the admin's follow-up list.
  // `level: 'high'` is shown in red, 'medium' in yellow.
  followUp: (answers) => {
    const reasons = [];
    if (answers.contact === 'yes') reasons.push({ label: 'Wants to be contacted', level: 'high' });
    if (answers.continue === 'considering_stopping') reasons.push({ label: 'Considering stopping', level: 'high' });
    if (answers.continue === 'unsure') reasons.push({ label: 'Unsure about continuing', level: 'medium' });
    return reasons;
  }
};

// ── Helpers ─────────────────────────────────────────────────────────────────

// Text in the chosen language, falling back to English if the slot is empty.
export function tr(text, lang) {
  if (!text) return '';
  return (lang && text[lang]) || text.en || '';
}

// True only when every text in the survey has the given language filled in.
export function hasLanguage(survey, lang) {
  const texts = [survey.title, survey.intro, survey.startLabel, survey.thankYou];
  if (survey.card) texts.push(...Object.values(survey.card));
  if (survey.ui) texts.push(...Object.values(survey.ui));
  survey.questions.forEach(q => {
    texts.push(q.text);
    if (q.hint) texts.push(q.hint);
    (q.options || []).forEach(o => texts.push(o.label));
  });
  return texts.every(t => t && typeof t[lang] === 'string' && t[lang].trim() !== '');
}

// English label for a saved answer — used in the admin view.
export function answerLabel(question, answer, answers = {}) {
  if (answer === undefined || answer === null || answer === '') return '—';
  if (question.type === 'text') return String(answer);
  const labelFor = (value) => {
    const option = (question.options || []).find(o => o.value === value);
    let label = option ? option.label.en : value;
    if (option?.withText && answers[`${question.id}_other`]) {
      label += `: ${answers[`${question.id}_other`]}`;
    }
    return label;
  };
  if (Array.isArray(answer)) return answer.length ? answer.map(labelFor).join(', ') : '—';
  return labelFor(answer);
}

// ── Monthly timing ──────────────────────────────────────────────────────────
// The monthly survey opens on the 25th and stays open until the 7th of the
// next month, so learners who miss the end of the month can still answer.
// Answers given in that window count for the month in which it opened.
export const MONTHLY_OPEN_DAY = 25;
export const MONTHLY_LAST_DAY_NEXT_MONTH = 7;

// "Later" hides the card for 3 days; after 3 "Later"s it stops asking that month.
export const MONTHLY_SNOOZE_DAYS = 3;
export const MONTHLY_MAX_SNOOZES = 3;

// Period used for testing (see MonthlySurveyCard). Admins can delete these.
export const PREVIEW_PERIOD = 'preview';

function formatPeriod(year, monthIndex) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

// 'YYYY-MM' if the survey is open on this date, otherwise null.
export function getMonthlySurveyPeriod(now = new Date()) {
  const day = now.getDate();
  if (day >= MONTHLY_OPEN_DAY) return formatPeriod(now.getFullYear(), now.getMonth());
  if (day <= MONTHLY_LAST_DAY_NEXT_MONTH) {
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return formatPeriod(prev.getFullYear(), prev.getMonth());
  }
  return null;
}

// The most recent month whose survey has already opened — the admin default.
export function getLatestOpenedPeriod(now = new Date()) {
  if (now.getDate() >= MONTHLY_OPEN_DAY) return formatPeriod(now.getFullYear(), now.getMonth());
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return formatPeriod(prev.getFullYear(), prev.getMonth());
}

// The last `count` periods, newest first — for the admin month picker.
export function getRecentPeriods(count = 12, now = new Date()) {
  const latest = getLatestOpenedPeriod(now);
  const [y, m] = latest.split('-').map(Number);
  const periods = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(y, m - 1 - i, 1);
    periods.push({
      value: formatPeriod(d.getFullYear(), d.getMonth()),
      label: d.toLocaleString('en-GB', { month: 'long', year: 'numeric' })
    });
  }
  return periods;
}

// Firestore document id — one response per learner per month.
export function monthlyResponseId(period, userId) {
  return `monthly_${period}_${userId}`;
}